import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Scale, BookOpen, Gavel, ScrollText, Landmark, ShieldCheck, PenTool, Search, Quote } from 'lucide-react';

const LawSchool: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "School of Jurisprudence";
    
    return (
        <div className="min-h-screen bg-[#fcfcfc] font-serif text-slate-900 selection:bg-[#8b0000] selection:text-white border-t-[6px] border-[#8b0000]">
            {/* Top Utility Bar */}
            <div className="bg-[#f2f2f2] border-b border-slate-200 py-2 px-8 flex justify-end gap-6 font-sans text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <a href="#" className="hover:text-[#8b0000]">Library</a>
                <a href="#" className="hover:text-[#8b0000]">Alumni</a>
                <a href="#" className="hover:text-[#8b0000]">Careers</a>
                <a href="#" className="hover:text-[#8b0000]">Giving</a>
            </div>

            {/* Logo / Header Area */}
            <header className="px-8 py-10 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-[#8b0000] text-white flex items-center justify-center rounded-sm">
                            <Scale size={28} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold uppercase tracking-wide text-slate-900 leading-none">{schoolName}</h1>
                            <span className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-1 block">Est. 1817 • Veritas et Justitia</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 md:w-64">
                         <Search size={14} className="text-slate-400" />
                         <input type="text" placeholder="Search..." className="bg-transparent border-none text-xs font-sans h-8 w-full focus:outline-none" />
                    </div>
                </div>
            </header>

            {/* Main Navigation */}
            <nav className="sticky top-0 z-50 bg-[#8b0000] text-white shadow-md">
                <div className="max-w-7xl mx-auto px-8">
                    <div className="flex justify-center md:justify-start">
                        {[
                            "Admissions", "Academics", "Faculty & Research", "Student Life", "Clinics", "News"
                        ].map((item, i) => (
                            <a key={i} href="#" className="px-6 py-4 font-sans text-xs font-bold uppercase tracking-widest hover:bg-[#600000] transition-colors border-r border-[#9e2222] last:border-r-0">
                                {item}
                            </a>
                        ))}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-[#e8e4dc] relative overflow-hidden min-h-[600px] flex items-center">
                 {/* Background Pattern */}
                 <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#000_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                 
                 <div className="max-w-7xl mx-auto px-8 grid grid-cols-1 md:grid-cols-2 gap-16 items-center w-full relative z-10">
                     <div>
                         <span className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-[#8b0000] mb-6 block">Dean's message</span>
                         <h2 className="text-5xl md:text-6xl font-black mb-8 leading-tight text-slate-900">
                             Defending the <br/>
                             Rule of Law.
                         </h2>
                         <p className="text-lg text-slate-700 leading-relaxed max-w-md mb-10 font-medium">
                             We educate lawyers who will lead the profession with integrity and intellectual rigor. Join a community dedicated to justice.
                         </p>
                         <Button className="font-sans bg-[#8b0000] hover:bg-[#600000] text-white font-bold uppercase tracking-widest text-xs h-12 px-8 rounded-sm shadow-md">
                             JD Application
                         </Button>
                     </div>
                     
                     <div className="relative h-[450px] w-full bg-white p-2 shadow-xl rotate-1 hover:rotate-0 transition-transform duration-500">
                         <div className="h-full w-full relative">
                             <img 
                                src="https://images.unsplash.com/photo-1589829085413-56de8ae18c73?q=80&w=2012&auto=format&fit=crop" 
                                alt="Law Library" 
                                className="w-full h-full object-cover grayscale brightness-110" 
                             />
                             <div className="absolute bottom-0 left-0 bg-[#8b0000] text-white p-6 max-w-xs">
                                 <Quote size={20} className="mb-2 opacity-50" />
                                 <p className="font-sans text-xs leading-relaxed italic">
                                     "Injustice anywhere is a threat to justice everywhere."
                                 </p>
                             </div>
                         </div>
                     </div>
                 </div>
            </div>

            {/* News / Features Grid */}
            <section className="py-20 px-8 max-w-7xl mx-auto">
                <div className="flex items-center gap-4 mb-12 border-b border-slate-200 pb-4">
                    <ScrollText className="text-[#8b0000]" />
                    <h3 className="text-2xl font-bold">Latest Perspectives</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                     {[
                         { cat: "Constitutional Law", title: "The Supreme Court's New Term: A Preview", date: "Oct 12, 2026" },
                         { cat: "International Rights", title: "Global Summit on Human Rights Policy", date: "Oct 10, 2026" },
                         { cat: "Corporate Governance", title: "Mergers and Acquisitions in the Tech Sector", date: "Oct 08, 2026" },
                     ].map((item, i) => (
                         <div key={i} className="group cursor-pointer">
                             <div className="h-48 bg-slate-200 mb-6 relative overflow-hidden">
                                 <div className="absolute inset-0 bg-[#8b0000] opacity-0 group-hover:opacity-10 transition-opacity"></div>
                                 <img src={`https://images.unsplash.com/photo-1505664194779-8beaceb93744?q=80&w=2070&auto=format&fit=crop&sig=${i}`} className="w-full h-full object-cover grayscale" />
                             </div>
                             <div className="font-sans text-[10px] font-bold uppercase tracking-widest text-[#8b0000] mb-2">{item.cat}</div>
                             <h4 className="text-xl font-bold leading-tight mb-3 group-hover:text-[#8b0000] transition-colors">{item.title}</h4>
                             <div className="font-sans text-xs text-slate-400">{item.date}</div>
                         </div>
                     ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1e1e1e] text-slate-400 py-16 px-8 border-t-4 border-[#8b0000]">
                 <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div className="col-span-1 md:col-span-2">
                         <div className="flex items-center gap-3 mb-6 text-white">
                             <Landmark size={24} />
                             <span className="font-bold text-xl uppercase tracking-wide">{schoolName}</span>
                         </div>
                         <p className="font-sans text-xs leading-relaxed max-w-sm mb-6">
                             123 Legal Avenue, Cambridge, MA 02138 <br/>
                             +1 (617) 555-0199
                         </p>
                         <div className="flex gap-4">
                             <Button size="sm" variant="outline" className="font-sans border-slate-600 text-slate-300 hover:bg-white hover:text-black uppercase tracking-widest text-[10px] h-8">Contact Admissions</Button>
                         </div>
                     </div>
                     
                     <div>
                         <h5 className="text-white font-bold mb-6 font-sans text-xs uppercase tracking-widest">Academics</h5>
                         <ul className="space-y-3 font-sans text-xs">
                             <li><a href="#" className="hover:text-white hover:underline">Juris Doctor (JD)</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">LL.M. Program</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">S.J.D. Program</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">Joint Degrees</a></li>
                         </ul>
                     </div>
                     
                     <div>
                         <h5 className="text-white font-bold mb-6 font-sans text-xs uppercase tracking-widest">Resources</h5>
                         <ul className="space-y-3 font-sans text-xs">
                             <li><a href="#" className="hover:text-white hover:underline">Pro Bono</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">Journals</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">Accessibility</a></li>
                             <li><a href="#" className="hover:text-white hover:underline">Privacy Policy</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto border-t border-slate-800 mt-12 pt-8 text-center font-sans text-[10px] uppercase tracking-widest">
                     © 2026 {schoolName}. All rights reserved.
                 </div>
            </footer>
        </div>
    );
};

export default LawSchool;
